import qs from 'query-string';

import { TMVideoType, VideoType } from '@/modules/common/types/video';

/**
 * Types
 */
type SourceUrl = string;
type MediaId = string;

type Resource = {
  signedUrlExp: number;
  mediaIds: Set<MediaId>;
};

type VideoCache = Record<MediaId, Set<SourceUrl>>;
type SourceCache = Record<SourceUrl, Resource>;

export const sourceCache: SourceCache = {};
export const videoCache: VideoCache = {};

export const isSignedUrl = (sourceUrl: SourceUrl): boolean => !!sourceCache[sourceUrl]?.signedUrlExp;

export const getAllVideoResources = (sourceUrl: SourceUrl): Array<{ mediaId: MediaId; sourceUrl: SourceUrl }> => {
  if (!sourceCache[sourceUrl]) return [];
  const mediaIds = Array.from(sourceCache[sourceUrl].mediaIds);
  return mediaIds.reduce(
    (acc, mediaId) => acc.concat([...videoCache[mediaId]].map((sourceUrl$) => ({ mediaId, sourceUrl: sourceUrl$ }))),
    [] as Array<{ mediaId: MediaId; sourceUrl: SourceUrl }>,
  );
};

export const setSourceCache = (mediaId: MediaId, sourceUrl: SourceUrl, signedUrlExp: number): void => {
  if (!sourceCache[sourceUrl]) {
    sourceCache[sourceUrl] = { signedUrlExp, mediaIds: new Set([mediaId]) };
  } else {
    sourceCache[sourceUrl].signedUrlExp = signedUrlExp;
    sourceCache[sourceUrl].mediaIds.add(mediaId);
  }

  if (videoCache[mediaId] && !videoCache[mediaId].has(sourceUrl)) {
    videoCache[mediaId].add(sourceUrl);
  } else if (!videoCache[mediaId]) {
    videoCache[mediaId] = new Set([sourceUrl]);
  }
};

export const updateSourceCache = (
  mediaId: MediaId,
  oldSourceUrl: SourceUrl,
  newSourceUrl: SourceUrl,
  signedUrlExp: number,
): void => {
  delete sourceCache[oldSourceUrl];
  videoCache[mediaId].delete(oldSourceUrl);
  setSourceCache(mediaId, newSourceUrl, signedUrlExp);
};

/**
 * Source validation and resource update management
 */
let timer: number | undefined;
let sourcesToDispatch: Array<{ mediaId: MediaId; sourceUrl: SourceUrl }> = [];

export function validateSourceCache(
  sourceUrl: SourceUrl,
  updateResourcesStartedAction: (resources: { mediaId: string; sourceUrl: string }[]) => void,
): void {
  const signedUrlExp = sourceCache[sourceUrl]?.signedUrlExp;
  if (signedUrlExp && signedUrlExp <= Math.floor(Date.now() / 1000)) {
    const resources = getAllVideoResources(sourceUrl);
    sourcesToDispatch = sourcesToDispatch.concat(resources);
    clearTimeout(timer!);
    timer = window.setTimeout(() => {
      updateResourcesStartedAction(sourcesToDispatch);
      sourcesToDispatch = [];
    }, 300);
  }
}

/**
 * Extracts source URLs from video and caches them.
 */
export const grepVideoResources = (video: TMVideoType, signedUrlExp: number): void => {
  const iterateVideoKey = (param: unknown): void => {
    if (Array.isArray(param)) {
      param.forEach((item) => iterateVideoKey(item));
    } else if (typeof param === 'object' && param !== null) {
      Object.keys(param).forEach((key) => iterateVideoKey((param as Record<string, unknown>)[key]));
    } else if (typeof param === 'string') {
      try {
        const url = new URL(param);
        const parsedSearchParams = qs.parse(url.search);
        const numericValues = Object.values(parsedSearchParams).map((value) => parseInt(value as string, 10));

        if (numericValues.includes(signedUrlExp)) {
          setSourceCache(video.mediaid, param, signedUrlExp);
        }
      } catch {
        // ignore
      }
    }
  };
  iterateVideoKey(video);
};

/**
 * Updates video URLs from resource map.
 */
export const updateVideoResources = (
  video: VideoType,
  resourceMap: Record<string, { newUrl: string; signedUrlExp: number }>,
): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateValue = (value: any): any => {
    if (typeof value === 'string' && resourceMap[value]) {
      updateSourceCache(video.mediaid, value, resourceMap[value].newUrl, resourceMap[value].signedUrlExp);
      return resourceMap[value].newUrl;
    }
    if (Array.isArray(value)) return value.map(updateValue);
    if (typeof value === 'object' && value !== null) {
      Object.keys(value).forEach((key) => {
        // eslint-disable-next-line no-param-reassign
        value[key] = updateValue(value[key]);
      });
    }
    return value;
  };

  Object.keys(video).forEach((key) => {
    // @ts-expect-error todo: fix me
    // eslint-disable-next-line no-param-reassign
    video[key] = updateValue(video[key]);
  });
};
